/**
 * Pure, framework-free mirror of the backend's owner-lock write gate
 * (`aistudiolib.api._assert_project_writable` / `set_project_locked`): given the current
 * user's email and a project's owner + lock state, decide who may write to it.
 *
 * Comparison is case-insensitive to match the backend's defensiveness against any
 * email-casing drift between identity providers (Cognito -> Okta migration).
 */
export interface ProjectPermissions {
  /** True when the current user is this project's owner (`user_name` on the project row). */
  readonly isOwner: boolean;
  /** True when the project is currently locked by its owner. */
  readonly isLocked: boolean;
  /** True when the current user may perform write operations right now. */
  readonly canWrite: boolean;
  /** Owner's email, or null if unknown (e.g. project not loaded yet). */
  readonly ownerEmail: string | null;
  /** Owner's email while locked, or null when unlocked/unknown - mirrors the API's `locked_by`. */
  readonly lockedBy: string | null;
}

/** Permissions to use while the project (or the current user) hasn't loaded yet - read-only by default, safest fallback. */
export const UNKNOWN_PROJECT_PERMISSIONS: ProjectPermissions = {
  isOwner: false,
  isLocked: false,
  canWrite: false,
  ownerEmail: null,
  lockedBy: null
};

const normalizeEmail = (email: string | null | undefined): string => (email ?? "").trim().toLowerCase();

export function computeProjectPermissions(
  currentUserEmail: string | null | undefined,
  ownerEmail: string | null | undefined,
  isLocked: boolean | null | undefined,
  lockedBy?: string | null
): ProjectPermissions {
  const normalizedCurrent = normalizeEmail(currentUserEmail);
  const normalizedOwner = normalizeEmail(ownerEmail);
  const isOwner = normalizedCurrent.length > 0 && normalizedOwner.length > 0 && normalizedCurrent === normalizedOwner;
  const locked = isLocked === true;

  return {
    isOwner,
    isLocked: locked,
    canWrite: !locked || isOwner,
    ownerEmail: ownerEmail ?? null,
    lockedBy: lockedBy ?? null
  };
}
